import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Eye, EyeOff, Lock, CheckCircle, LockKeyholeIcon, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import api from "../../lib/api";
import toast from "react-hot-toast";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Invalid reset link");
      navigate("/login");
      return;
    }

    // Verify token on component mount
    const verifyToken = async () => {
      try {
        await api.get(`/auth/verify-reset-token/${token}`);
        setIsTokenValid(true);
      } catch (error) {
        setIsTokenValid(false);
        toast.error(error.response?.data?.message || "Invalid or expired reset link");
      }
    };

    verifyToken();
  }, [token, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (formData.password.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }

    setIsLoading(true);

    try {
      await api.post("/auth/reset-password", {
        token,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
      });

      setIsSuccess(true);
      toast.success("Password reset successfully!");

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  if (isTokenValid === false) {
    return (
      <div className="before:contents-[''] relative flex min-h-screen items-center justify-center bg-navy-900 bg-opacity-90 py-20 before:absolute before:inset-0 before:z-[-1] before:bg-login-screen before:bg-cover before:bg-center before:bg-no-repeat">
        <div className="w-full max-w-md px-4">
          {/* Logo and Header */}
          <div className="fade-in mb-12 px-4 text-center">
            {/* Logo */}
            <img src="/images/logo-white.svg" alt="Minesure Logo" className="w-full" />
          </div>
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/20">
            <Lock className="h-8 w-8 text-red-400" />
          </div>
          <h1 className="mb-7 text-center text-3xl font-bold text-white">Invalid Link</h1>
          <p className="mb-9 flex items-center justify-center text-center text-sm font-normal uppercase text-gray-400 transition-colors">
            This password reset link is invalid or has expired.
          </p>
          <Link
            to="/forgot-password"
            className="flex h-16 w-full items-center justify-center rounded-xl bg-red-500 text-sm font-normal uppercase text-gray-0 shadow-lg hover:shadow-xl"
          >
            Request New Reset Link
          </Link>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="before:contents-[''] relative flex min-h-screen items-center justify-center bg-navy-900 bg-opacity-90 py-20 before:absolute before:inset-0 before:z-[-1] before:bg-login-screen before:bg-cover before:bg-center before:bg-no-repeat">
        <div className="w-full max-w-md px-4">
          {/* Logo and Header */}
          <div className="fade-in mb-12 px-4 text-center">
            {/* Logo */}
            <img src="/images/logo-white.svg" alt="Minesure Logo" className="w-full" />
          </div>
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20">
            <CheckCircle className="h-8 w-8 text-green-400" />
          </div>
          <h1 className="mb-7 text-center text-3xl font-bold text-white">
            Password Reset Successful
          </h1>
          <p className="mb-9 flex items-center justify-center text-center text-sm font-normal uppercase text-gray-400 transition-colors">
            Your password has been successfully reset. You will be redirected to the login page
            shortly.
          </p>
          <Link
            to="/login"
            className="flex h-16 w-full items-center justify-center rounded-xl bg-red-500 text-sm font-normal uppercase text-gray-0 shadow-lg hover:shadow-xl"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  if (isTokenValid === null) {
    return (
      <div className="before:contents-[''] relative flex min-h-screen items-center justify-center bg-navy-900 bg-opacity-90 py-20 before:absolute before:inset-0 before:z-[-1] before:bg-login-screen before:bg-cover before:bg-center before:bg-no-repeat">
        <div className="w-full max-w-md px-4">
          {/* Logo and Header */}
          <div className="fade-in mb-12 px-4 text-center">
            {/* Logo */}
            <img src="/images/logo-white.svg" alt="Minesure Logo" className="w-full" />
          </div>
          <div className="mx-auto mb-7 h-8 w-8 animate-spin rounded-full border-b-2 border-white"></div>
          <p className="mb-9 text-center text-sm font-normal uppercase text-gray-0">
            Verifying reset link...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="before:contents-[''] relative flex min-h-screen items-center justify-center bg-navy-900 bg-opacity-90 py-20 before:absolute before:inset-0 before:z-[-1] before:bg-login-screen before:bg-cover before:bg-center before:bg-no-repeat">
      <div className="w-full max-w-md px-4">
        {/* Logo and Header */}
        <div className="fade-in mb-12 px-4 text-center">
          {/* Logo */}
          <img src="/images/logo-white.svg" alt="Minesure Logo" className="w-full" />
        </div>
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-500/20">
          <LockKeyholeIcon className="h-8 w-8 text-blue-500" />
        </div>
        <h1 className="mb-7 text-center text-3xl font-bold text-white">Reset Password</h1>
        <p className="mb-9 flex items-center justify-center text-center text-sm font-normal uppercase text-gray-0 transition-colors">
          Enter your new password below.
        </p>

        {/* Reset Password Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* New Password Field */}
          <div className="relative h-16 w-full">
            <label
              htmlFor="password"
              className="absolute left-4 top-2.5 z-10 m-0 block text-xxs font-normal uppercase leading-3 text-navy-900"
            >
              New Password:
            </label>
            <Input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              className="text-color-gray-0 absolute inset-0 h-auto rounded-xl border-0 bg-gray-0 px-4 pt-6 hover:outline-none focus:border-0 focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 transform text-navy-900 hover:text-gray-600"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          {/* Confirm Password Field */}
          <div className="relative h-16 w-full">
            <label
              htmlFor="confirmPassword"
              className="absolute left-4 top-2.5 z-10 m-0 block text-xxs font-normal uppercase leading-3 text-navy-900"
            >
              Confirm New Password:
            </label>
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={handleChange}
              className="text-color-gray-0 absolute inset-0 h-auto rounded-xl border-0 bg-gray-0 px-4 pt-6 hover:outline-none focus:border-0 focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 transform text-navy-900 hover:text-gray-600"
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isLoading}
            className="h-16 w-full rounded-xl bg-red-500 text-sm font-normal uppercase text-gray-0 shadow-lg hover:shadow-xl"
          >
            {isLoading ? (
              <div className="flex items-center">
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                Resetting...
              </div>
            ) : (
              <div className="flex items-center justify-center">Reset Password</div>
            )}
          </Button>

          {/* Back to Login Link */}
          <div className="text-center">
            <Link
              to="/login"
              className="flex items-center justify-center text-sm font-normal uppercase text-gray-0 transition-colors hover:text-red-600"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
